import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { AttioConsultantData, AttioConsultantService } from 'dashboard409a/services/attio/AttioConsultantService';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';

interface UseOtherConsultantsProps {
  valuationRow: SimplifiedAttioValuation;
  onOtherConsultantsUpdate?: (
    recordId: string,
    otherConsultantOptions: { targetRecordId: string; name: string; imageSrc: string }[]
  ) => Promise<void>;
}

export const useOtherConsultants = ({ valuationRow, onOtherConsultantsUpdate }: UseOtherConsultantsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [consultants, setConsultants] = useState<AttioConsultantData[]>([]);
  const [selectedConsultantIds, setSelectedConsultantIds] = useState<string[]>([]);
  const consultantService = useMemo(() => new AttioConsultantService(), []);
  const { enqueueSnackbar } = useSnackbar();

  const updateMutation = useMutation({
    mutationFn: ({
      valuationId,
      otherConsultantTargetRecordIds,
    }: {
      valuationId: string;
      otherConsultantTargetRecordIds: string[];
    }) => consultantService.updateValuationOtherConsultants({ valuationId, otherConsultantTargetRecordIds }),
    onSuccess: async (_, variables) => {
      if (onOtherConsultantsUpdate) {
        const options = selectedConsultantIds.map(id => {
          const c = consultants.find(x => (x as any).targetRecordId === id);
          return {
            targetRecordId: id,
            name: c?.name || '',
            imageSrc: (c as any)?.imageSrc || '',
          };
        });
        await onOtherConsultantsUpdate(variables.valuationId, options);
      }
      enqueueSnackbar('Other consultants updated successfully!', { variant: 'success' });
      setAnchorEl(null);
      setSearchTerm('');
      setConsultants([]);
      setSelectedConsultantIds([]);
    },
    onError: () => {
      enqueueSnackbar('Failed to update other consultants. Please try again.', { variant: 'error' });
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
    setConsultants([]);
    setSelectedConsultantIds([]);
  };

  const handleToggleConsultant = (targetRecordId: string) => {
    setSelectedConsultantIds(prev =>
      prev.includes(targetRecordId) ? prev.filter(id => id !== targetRecordId) : [...prev, targetRecordId]
    );
  };

  const handleSave = () => {
    if (selectedConsultantIds.length === 0) {
      return;
    }
    updateMutation.mutate({
      valuationId: valuationRow.record_id,
      otherConsultantTargetRecordIds: selectedConsultantIds,
    });
  };

  const handleSearch = async () => {
    setSearching(true);
    if (searchTerm?.trim()) {
      const result = await consultantService.searchConsultants(searchTerm);
      setConsultants(result as any);
      setSearching(false);
    } else {
      setSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchTerm.trim() && !searching) {
      handleSearch();
    }
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'other-consultant-popover' : undefined;

  return {
    anchorEl,
    searchTerm,
    searching,
    consultants,
    selectedConsultantIds,
    open,
    popoverId,
    handleClick,
    handleClose,
    handleToggleConsultant,
    handleSave,
    handleSearch,
    handleKeyDown,
    setSearchTerm,
  };
};
